import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaHome, FaUsers, FaMoneyBillWave, FaUserCog, FaSignOutAlt, FaClipboardList, FaUserGraduate, FaFileSignature, FaUserPlus } from "react-icons/fa";
import ismanizlogo from '../assets/nizlogo.png';

const Sidebar = ({ user }) => {
  const location = useLocation();
  const [activeItem, setActiveItem] = useState(getInitialActiveItem(location.pathname));
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Desktop'ta açık, mobilde kapalı başla
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1024; // lg breakpoint
    }
    return false;
  });

  // Kullanıcı rolüne göre admin kontrolü
  useEffect(() => {
    // user prop'undan veya localStorage'dan rolü kontrol et
    const userRole = user?.role || localStorage.getItem("userRole");
    setIsAdmin(userRole === "admin");
  }, [user]);

  // Ekran boyutu değiştiğinde sidebar durumunu ayarla
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        // Desktop'ta sidebar açık olmalı
        setIsCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function getInitialActiveItem(pathname) {
    if (pathname.includes('/dashboard')) return 'dashboard';
    if (pathname.includes('/student-list/student-reg')) return 'student-reg';
    if (pathname.includes('/student-list')) return 'student-list';
    if (pathname.includes('/financial')) return 'financial';
    if (pathname.includes('/admin')) return 'admin';
    if (pathname.includes('/exam-registered-list')) return 'exam-list';
    if (pathname.includes('/sinav-kayit')) return 'sinav-kayit';
    return 'dashboard';
  }

  const handleItemClick = (itemName) => {
    setActiveItem(itemName);
  };

  const handleLogout = () => {
    // LocalStorage'dan token ve rol bilgilerini temizle
    localStorage.removeItem("authToken");
    localStorage.removeItem("userRole");
    // Login sayfasına yönlendir
    window.location.href = "/login";
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      {/* Sidebar toggle button for mobile */}
      <button 
        onClick={toggleSidebar}
        className={`lg:hidden fixed top-4 left-4 z-50 bg-indigo-600 text-white p-2 rounded-full shadow-lg transition-all duration-300 ${
          !isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      
      {/* Close button inside sidebar for mobile */}
      {!isCollapsed && (
        <button 
          onClick={toggleSidebar}
          className="lg:hidden absolute top-4 right-4 z-50 bg-red-600 text-white p-2 rounded-full shadow-lg hover:bg-red-700 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      {/* Sidebar */}
      <div className={`${isCollapsed ? '-translate-x-full' : 'translate-x-0'} lg:translate-x-0 transform transition-transform duration-300 ease-in-out bg-gradient-to-b from-gray-900 to-indigo-900 text-white w-64 flex flex-col h-screen fixed left-0 top-0 z-40 shadow-2xl`}>
        {/* Logo ve Başlık */}
        <div className="p-3 sm:p-5 border-b border-indigo-800 flex items-center justify-center bg-gradient-to-r from-indigo-700 to-purple-700">
          <img
            src={ismanizlogo}
            alt="ESN Akademi Logo"
            className="h-12 sm:h-18 mr-2 sm:mr-3 drop-shadow-lg"
          />
        </div>

        {/* Kullanıcı Bilgisi */}
        <div className="p-3 sm:p-5 border-b border-indigo-800 bg-indigo-800 bg-opacity-30">
          <div className="flex items-center mb-2 sm:mb-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
              <span className="text-sm sm:text-lg font-bold">{user.name?.charAt(0) || "U"}</span>
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="font-semibold text-white text-sm sm:text-base">{user.name}</p>
              <p className="text-xs text-indigo-200">{isAdmin ? 'Yönetici' : 'Personel'}</p>
            </div>
          </div>
        </div>

        {/* Navigasyon Menüsü */}
        <nav className="flex-1 overflow-y-auto py-2 sm:py-4 bg-opacity-10 bg-indigo-900">
          <ul className="space-y-1 px-2 sm:px-3">
            {/* Ana Sayfa / Dashboard - Sadece adminler görebilir */}
            {isAdmin && (
              <li>
                <Link
                  to="/dashboard"
                  className={`flex items-center px-2 sm:px-4 py-2 sm:py-3 rounded-lg transition-all duration-200 text-sm sm:text-base ${
                    activeItem === 'dashboard'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 shadow-md'
                      : 'hover:bg-indigo-800 hover:bg-opacity-50'
                  }`}
                  onClick={() => {
                    handleItemClick('dashboard');
                    setIsCollapsed(true); // Mobilde tıklayınca sidebar'ı kapat
                  }}
                >
                  <FaHome className={`mr-2 sm:mr-3 text-lg sm:text-xl ${activeItem === 'dashboard' ? 'text-white' : 'text-indigo-300'}`} />
                  <span className={`${activeItem === 'dashboard' ? 'font-semibold' : ''}`}>Ana Sayfa</span>
                </Link>
              </li>
            )}

            {/* Öğrenciler */}
            <li>
              <Link
                to="/student-list"
                className={`flex items-center px-2 sm:px-4 py-2 sm:py-3 rounded-lg transition-all duration-200 text-sm sm:text-base ${
                  activeItem === 'student-list'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 shadow-md'
                    : 'hover:bg-indigo-800 hover:bg-opacity-50'
                }`}
                onClick={() => {
                  handleItemClick('student-list');
                  setIsCollapsed(true);
                }}
              >
                <FaUserGraduate className={`mr-2 sm:mr-3 text-lg sm:text-xl ${activeItem === 'student-list' ? 'text-white' : 'text-indigo-300'}`} />
                <span className={`${activeItem === 'student-list' ? 'font-semibold' : ''}`}>Öğrenciler</span>
              </Link>
            </li>

            {/* Öğrenci Kayıt */}
            <li>
              <Link
                to="/student-list/student-reg"
                className={`flex items-center px-2 sm:px-4 py-2 sm:py-3 rounded-lg transition-all duration-200 text-sm sm:text-base ${
                  activeItem === 'student-reg'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 shadow-md'
                    : 'hover:bg-indigo-800 hover:bg-opacity-50'
                }`}
                onClick={() => {
                  handleItemClick('student-reg');
                  setIsCollapsed(true);
                }}
              >
                <FaUserPlus className={`mr-2 sm:mr-3 text-lg sm:text-xl ${activeItem === 'student-reg' ? 'text-white' : 'text-indigo-300'}`} />
                <span className={`${activeItem === 'student-reg' ? 'font-semibold' : ''}`}>Öğrenci Kayıt</span>
              </Link>
            </li>

            {/* Finansal - Sadece adminler görebilir */}
            {isAdmin && (
              <li>
                <Link
                  to="/financial"
                  className={`flex items-center px-2 sm:px-4 py-2 sm:py-3 rounded-lg transition-all duration-200 text-sm sm:text-base ${
                    activeItem === 'financial'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 shadow-md'
                      : 'hover:bg-indigo-800 hover:bg-opacity-50'
                  }`}
                  onClick={() => {
                    handleItemClick('financial');
                    setIsCollapsed(true);
                  }}
                >
                  <FaMoneyBillWave className={`mr-2 sm:mr-3 text-lg sm:text-xl ${activeItem === 'financial' ? 'text-white' : 'text-indigo-300'}`} />
                  <span className={`${activeItem === 'financial' ? 'font-semibold' : ''}`}>Finansal</span>
                </Link>
              </li>
            )}
            
            {/* Sınav Listesi - Sadece adminler görebilir */}
            {isAdmin && (
              <li>
                <Link
                  to="/exam-registered-list"
                  className={`flex items-center px-2 sm:px-4 py-2 sm:py-3 rounded-lg transition-all duration-200 text-sm sm:text-base ${
                    activeItem === 'exam-list'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 shadow-md'
                      : 'hover:bg-indigo-800 hover:bg-opacity-50'
                  }`}
                  onClick={() => {
                    handleItemClick('exam-list');
                    setIsCollapsed(true);
                  }}
                >
                  <FaClipboardList className={`mr-2 sm:mr-3 text-lg sm:text-xl ${activeItem === 'exam-list' ? 'text-white' : 'text-indigo-300'}`} />
                  <span className={`${activeItem === 'exam-list' ? 'font-semibold' : ''}`}>Sınav Listesi</span>
                </Link>
              </li>
            )}

            {/* Sınav Kayıt Formu - yalnızca admin */}
            {isAdmin && (
              <li>
                <Link
                  to="/sinav-kayit"
                  className={`flex items-center px-2 sm:px-4 py-2 sm:py-3 rounded-lg transition-all duration-200 text-sm sm:text-base ${
                    activeItem === 'sinav-kayit'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 shadow-md'
                      : 'hover:bg-indigo-800 hover:bg-opacity-50'
                  }`}
                  onClick={() => {
                    handleItemClick('sinav-kayit');
                    setIsCollapsed(true);
                  }}
                >
                  <FaFileSignature className={`mr-2 sm:mr-3 text-lg sm:text-xl ${activeItem === 'sinav-kayit' ? 'text-white' : 'text-indigo-300'}`} />
                  <span className={`${activeItem === 'sinav-kayit' ? 'font-semibold' : ''}`}>Sınav Kayıt</span>
                </Link>
              </li>
            )}

            {/* Admin Paneli - Sadece adminler görebilir */}
            {isAdmin && (
              <li>
                <Link
                  to="/admin"
                  className={`flex items-center px-2 sm:px-4 py-2 sm:py-3 rounded-lg transition-all duration-200 text-sm sm:text-base ${
                    activeItem === 'admin'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 shadow-md'
                      : 'hover:bg-indigo-800 hover:bg-opacity-50'
                  }`}
                  onClick={() => {
                    handleItemClick('admin');
                    setIsCollapsed(true);
                  }}
                >
                  <FaUserCog className={`mr-2 sm:mr-3 text-lg sm:text-xl ${activeItem === 'admin' ? 'text-white' : 'text-indigo-300'}`} />
                  <span className={`${activeItem === 'admin' ? 'font-semibold' : ''}`}>Admin Paneli</span>
                </Link>
              </li>
            )}
          </ul>
        </nav>

        {/* Çıkış Yap */}
        <div className="p-2 sm:p-4 border-t border-indigo-800 bg-indigo-900 bg-opacity-30">
          <button
            onClick={handleLogout}
            className="flex items-center px-2 sm:px-4 py-2 w-full rounded-lg text-indigo-200 hover:text-white hover:bg-red-600 transition-all duration-200 text-sm sm:text-base"
          >
            <FaSignOutAlt className="mr-2 sm:mr-3" />
            Çıkış Yap
          </button>
        </div>
      </div>

      {/* Sidebar backdrop for mobile */}
      {!isCollapsed && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={toggleSidebar}
        />
      )}
    </>
  );
};

export default Sidebar;
